import {loadIncompleteOperations} from "./loadIncompleteOperations";
import {createResurrectedFileOperationObject} from "./createResurrectedFileOperationObject";
import {executeApiRequest} from "../executeApiRequest";
import {ADD_OPERATION, UPDATE_OPERATION} from "../../app/AppContext";
import {kickOffUploadWorker} from "../../webworkers/fileuploads/kickOffUploadWorker";
import {getWorkerConfigBase} from "../../webworkers/getWorkerConfigBase";
import {scheduleOperation} from "../scheduleOperation";
import {registerOperationInDb} from "../registerOperationInDb";
import {clientLog} from "../../clientLog";
import {acquireJobLock, holdJobLockIfAvailable, tryJobLock} from "../uploadJobLock";
import {claimJob} from "./claimJob";
import {isReattachable} from "./isReattachable";
import {loadJobRerunState} from "./loadJobRerunState";

const MAX_RERUNS = 3;

let resurrection = null;

/**
 * Resurrects incomplete upload operations at most once per page load, however often it is called
 * (DE-1711: the triggering effect can fire more than once).
 *
 * @return {Promise<void>} The single resurrection run of this page load.
 */
export const resurrectOnce = (...args) => {
    resurrection ??= resurrectFileUploadOperations(...args);
    return resurrection;
};

/**
 * Loads incomplete operations from the server and adds them to the state.
 *
 * @param {boolean} userIsAuthenticated - Is the user authenticated?
 * @param {Object} userData - User data object containing the user ID and other
 *   information.
 * @param {Object[]} museumCollections - List of objects containing the
 *   collection ID of each museum collection.
 * @param {Function} appDispatch - The dispatch function for the app state.

 * @throws {Error} If userIsAuthenticated is false.
 * @throws {Error} If userData is not specified.
 * @throws {Error} If museumCollections is not specified or has no values.
 * @throws {Error} If appDispatch is not specified.
 *
 * @return {Promise<void>} A promise that resolves when the operations have been
 *   loaded and added to the state.
 */
export const resurrectFileUploadOperations = async (userIsAuthenticated,
                                                    userData,
                                                    museumCollections,
                                                    appDispatch) => {
    if (!userIsAuthenticated) {
        throw Error('User must be authenticated');
    }

    if (!userData || Object.keys(userData).length === 0) {
        throw Error('userData is not specified');
    }

    if (!museumCollections || museumCollections?.length === 0) {
        throw Error('museumCollections is not specified or has no values');
    }

    if (!appDispatch) {
        throw Error('appDispatch is not specified');
    }

    const data = await loadIncompleteOperations(museumCollections);
    if (!data || data?.length === 0) {
        return;
    }

    const resurrectableOperations = _getResurrectableOperations(data, museumCollections);
    if (resurrectableOperations?.length === 0) {
        return;
    }

    clientLog('info', `found ${resurrectableOperations.length} resurrectable operations`);

    const resumedOperations = [];

    // Reattach to each job that has files and has not failed (DE-1730, DE-1816, ADR-0010, ADR-0039), and replace
    // every error job this tab may take over (DE-1711, ADR-0006), at most MAX_RERUNS times (DE-1817):
    for (let i = 0, max = resurrectableOperations?.length; i < max; i++) {
        const operation = resurrectableOperations[i];
        const resumedOperation = isReattachable(operation)
            ? await _reattachOperation(appDispatch, operation)
            : await _replaceOperation(appDispatch, operation);
        if (!resumedOperation) {
            continue;
        }

        resumedOperations.push(resumedOperation);

        const apiGateway = window._env_.REACT_APP_DAMS_ADMIN_API;
        kickOffUploadWorker({
            workerConfig: getWorkerConfigBase(apiGateway),
            operationId: resumedOperation.jobId,
            operations: resumedOperations.filter(o => o.jobType === 'FILE_UPLOAD'),
            uploadWorkerDispatch: appDispatch
        });
    }
};

/**
 * Takes the original operation back into this tab, unchanged, when it is still processing or waiting for
 * review: its documents may exist and hold metadata, and the server may still be working, so nothing is claimed
 * or re-created. The tab holds the job's lock, as the
 * tab that started the job did, and skips the job when another tab of this browser holds it.
 *
 * @param {Function} appDispatch - The dispatch function for the app state.
 * @param {Object} operation - The original operation.
 * @returns {Promise<Object|null>} The operation to track, or null if another tab owns it.
 * @private
 */
const _reattachOperation = async (appDispatch, operation) => {
    const {jobId} = operation;
    if (!await holdJobLockIfAvailable(jobId)) {
        clientLog('info', `not reattaching ${jobId}: active in another tab`, 'resurrect');
        return null;
    }

    // Without the status from /jobs/incomplete: the worker's first status poll adds it, with its details, after
    // the worker's "workerstarted" message, so the operations list sees it as the latest message and offers
    // "Se igjennom" or, when timed out, "Lukk", as in the tab that started the job.
    const reattachedOperation = {...operation, workerMessages: []};
    appDispatch({
        type: ADD_OPERATION,
        operation: reattachedOperation
    });
    clientLog('info', `reattached ${jobId}`, 'resurrect');
    return reattachedOperation;
};

/**
 * Replaces the original operation with a new one carrying the same files, once this tab has claimed it, and
 * locks the replacement so that other tabs leave it alone. A job already re-run MAX_RERUNS times, or whose
 * re-run count cannot be read, is shown as failed instead, for the user to close (DE-1817).
 *
 * @param {Function} appDispatch - The dispatch function for the app state.
 * @param {Object} operation - The original operation.
 * @returns {Promise<Object|null>} The replacement operation, or null if the original was not claimed.
 * @private
 */
const _replaceOperation = async (appDispatch, operation) => {
    const {jobId: oldJobId, files} = operation;
    const rerunCount = await _nextRerunCount(oldJobId);
    if (rerunCount === null) {
        appDispatch({type: ADD_OPERATION, operation});
        return null;
    }

    if (!await _claimUnlockedOperation(oldJobId)) {
        return null;
    }

    const registeredOperation = await _registerOperation(appDispatch, operation, rerunCount);
    const rescheduledOperation = await _appendFilesAndRescheduleOperation(appDispatch, registeredOperation, files);

    await acquireJobLock(rescheduledOperation.jobId);
    return rescheduledOperation;
};

/**
 * Works out the re-run count of the replacement. A job whose failure was shown to the user is re-run without
 * counting; the cap beats that.
 *
 * @param {string} jobId - The original operation's job id.
 * @returns {Promise<number|null>} The replacement's count, or null when the job must not be re-run: the cap is
 *   reached or the state is unreadable (fail closed).
 * @private
 */
const _nextRerunCount = async (jobId) => {
    let state;
    try {
        state = await loadJobRerunState(jobId);
    } catch (error) {
        clientLog('warn', `not resurrecting ${jobId}: re-run state unreadable (${error})`, 'resurrect');
        return null;
    }
    if (state.rerunCount >= MAX_RERUNS) {
        clientLog('info', `not resurrecting ${jobId}: re-run limit reached`, 'resurrect');
        return null;
    }
    return state.failureShown ? state.rerunCount : state.rerunCount + 1;
};

/**
 * Takes over the original operation, so that this tab may replace it. Skips an operation that another tab
 * of this browser holds (it is still uploading or awaiting review there), and one the backend refuses to
 * claim because it is finished or another tab claimed it first. The claim also cancels the original.
 *
 * @param {string} jobId - The original operation's job id.
 * @returns {Promise<boolean>} True if this tab claimed the operation.
 */
const _claimUnlockedOperation = async (jobId) => {
    try {
        const {acquired, result: claimed} = await tryJobLock(jobId, () => claimJob(jobId));
        if (!acquired || !claimed) {
            clientLog('info', `not resurrecting ${jobId}: ${acquired ? 'claimed elsewhere or finished' : 'active in another tab'}`, 'resurrect');
        }
        return acquired && claimed;
    } catch (error) {
        clientLog('warn', `not resurrecting ${jobId}: claim failed (${error})`, 'resurrect');
        return false;
    }
};

/**
 * Registers a resurrected operation in the DAMS database, schedules the operation,
 * and adds the operation to the list of operations on the client.
 *
 * @param {function} appDispatch - Dispatch function to update the list of operations on the client.
 * @param {object} resurrectableOperation - Object containing the details of the operation to be resurrected.
 * @param {number} rerunCount - How often the job has now been re-run (DE-1817).
 *
 * @returns {Promise<object>} The promise that resolves with the registered operation object.
 *
 * @private
 */
const _registerOperation = async (appDispatch, resurrectableOperation, rerunCount) => {
    if (resurrectableOperation.jobId) {
        delete resurrectableOperation.jobId;
    }

    if (resurrectableOperation.jobType) {
        delete resurrectableOperation.jobType;
    }

    if (resurrectableOperation.files) {
        delete resurrectableOperation.files;
    }

    if (resurrectableOperation.workerMessages) {
        delete resurrectableOperation.workerMessages;
    }

    const res = await scheduleOperation(resurrectableOperation);
    const {operation: scheduledOperation} = res;

    // Register the operation in the DAMS database, to track its progress:
    const registered = await registerOperationInDb(scheduledOperation, {...resurrectableOperation, rerunCount});

    // Add the operation to the list of operations on the client:
    appDispatch({
        type: ADD_OPERATION,
        operation: registered
    });

    return registered;
};

/**
 * Appends the given list of files to the registered operation, and reschedules it.
 *
 * @param {function} appDispatch - The dispatch function of the app state.
 * @param {object} registeredOperation - The operation that has been registered.
 * @param {array<object>} files - The list of files to append to the operation.
 * @returns {Promise<object>} A promise that resolves with the rescheduled operation.
 */
const _appendFilesAndRescheduleOperation = async (appDispatch, registeredOperation, files) => {
    // Append the list of files to the operation, and reschedule it:
    const operation = {...registeredOperation, files};
    await executeApiRequest('add-files', operation)
    await executeApiRequest('modify', operation);

    // Update the operation on the client, with the new list of files:
    appDispatch({
        type: UPDATE_OPERATION,
        operation: operation
    });

    const rescheduled = await executeApiRequest('reschedule', operation);

    // Update the operation on the client, with the rescheduled operation:
    appDispatch({
        type: UPDATE_OPERATION,
        operation: rescheduled.operation
    });

    return rescheduled.operation;
};

/**
 * Creates an array of resurrectable operations from the given data and museum collections.
 *
 * @param {object[]} data An array of objects, each containing a job ID, collection ID, status and message of an operation.
 * @param {object[]} museumCollections An array of objects, each containing the collection ID and museum ID of a museum collection.
 *
 * @returns {object[]} An array of resurrectable operations, each with a jobId, collectionId, museumId and files property.
 */
const _getResurrectableOperations = (data, museumCollections) => {
    const operationObjects = [];
    for (let i = 0, max = data?.length; i < max; i++) {
        const operationObj = createResurrectedFileOperationObject(museumCollections, data[i]);
        if (operationObj) {
            operationObjects.push(operationObj);
        }
    }
    return operationObjects;
};

